import { useState } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, defineMessages } from 'react-intl';

import {
  Currency,
  FiatWalletAccountState,
} from '#src/__generated__/globalTypes.ts';
import { RadioGroup } from '#src/atoms/inputs/RadioGroup/index.tsx';
import { Alert } from '#src/atoms/ui/Alert/index.tsx';
import { CreateFiatWallet } from '#src/components/fiatWallet/CreateFiatWallet/index.tsx';
import { SettingsSection } from '#src/components/settings/SettingsSection/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useApplyJonumRestriction } from '#src/hooks/useApplyJonumRestriction.ts';
import { useFiatBalance } from '#src/hooks/wallets/useFiatBalance.ts';
import { cryptocurrencySymbols } from '#src/lib/cryptocurrency.ts';

const messages = defineMessages({
  cash: {
    defaultMessage: 'Cash ({currency})',
  },
});

type Props = {
  title: MessageDescriptor;
  name: string;
  value: Currency;
  onUpdate: (value: Currency) => void;
  disableCashForJonum?: boolean;
  allowCashWithoutWallet?: boolean;
  jonumBanner?: { title: MessageDescriptor; message: MessageDescriptor };
};

export const CashCryptoToggle = ({
  title,
  name,
  value,
  onUpdate,
  disableCashForJonum = false,
  allowCashWithoutWallet = false,
  jonumBanner,
}: Props) => {
  const {
    cryptocurrency,
    fiatCurrency,
    walletPreferences: { showCryptoWallet, showFiatWallet },
  } = useCurrentUserContext();
  const { canDepositAndWithdraw } = useFiatBalance();
  const { formatMessage } = useIntlContext();
  const isJonumSubject = useApplyJonumRestriction();
  const [activatingCashWallet, setActivatingCashWallet] = useState(false);

  const cashDisabled = disableCashForJonum && isJonumSubject;
  const cashNeedsActivation = !cashDisabled && !canDepositAndWithdraw;
  const cashSelected =
    value === Currency.FIAT &&
    !cashDisabled &&
    (allowCashWithoutWallet || canDepositAndWithdraw);
  const selectedValue = cashSelected ? Currency.FIAT : Currency.ETH;

  const onChange = (next: Currency) => {
    if (next === Currency.FIAT) {
      if (cashNeedsActivation) {
        setActivatingCashWallet(true);
        return;
      }
      if (value !== Currency.FIAT) {
        onUpdate(Currency.FIAT);
      }
      return;
    }
    if (value !== Currency.ETH) {
      onUpdate(Currency.ETH);
    }
  };

  const options = [
    {
      value: Currency.FIAT,
      label: formatMessage(messages.cash, { currency: fiatCurrency }),
      disabled: cashDisabled,
    },
    {
      value: Currency.ETH,
      label: cryptocurrencySymbols[cryptocurrency],
    },
  ];

  if (!showCryptoWallet || !showFiatWallet) return null;

  return (
    <SettingsSection title={title}>
      {jonumBanner && isJonumSubject ? (
        <Alert
          title={<FormattedMessage {...jonumBanner.title} />}
          message={<FormattedMessage {...jonumBanner.message} />}
        />
      ) : null}
      <RadioGroup
        withSpacing
        options={options}
        value={selectedValue}
        name={name}
        onChange={onChange}
      />
      {activatingCashWallet ? (
        <CreateFiatWallet
          statusTarget={FiatWalletAccountState.VALIDATED_OWNER}
          canDismissAfterActivation={false}
          onClose={() => setActivatingCashWallet(false)}
          onDismissActivationSuccess={() => setActivatingCashWallet(false)}
        />
      ) : null}
    </SettingsSection>
  );
};
